import {trim} from 'lodash';
import {HTTP_SERVICE_INSTANCE} from '@/services/http.service';
import moment from 'moment';
import {TimelineLifeCycleFeature} from '@/views/timeline/features/timeline-life-cycle-feature';
import Holidays from 'date-holidays';

export class MechanicRestWorkingHoursFeature implements TimelineLifeCycleFeature {

    private static readonly DOM_QUERY_SELECTOR = '[restworkinghours]';
    private static readonly DOM_ATTRIBUTE_SELECTOR = 'restworkinghours';

    private interval: number | undefined;

    initialize(
        timelineEl: HTMLElement,
        config: DisguiseDto.ITimelineFeatureConfig,
        repositionGroupCB: () => void
    ) {
        setTimeout(() => {
            const castedConfig = config as DisguiseDto.TimelineRestWorkingHoursFeatureConfigDto;

            const allMatchingElements = timelineEl.querySelectorAll(MechanicRestWorkingHoursFeature.DOM_QUERY_SELECTOR);
            // 1. find all relevant users from DOM
            // 2. request online status
            // 3. inject online status

            const fastElMap: { [username: string]: HTMLElement } = {};

            for (const currentMatchingEl of allMatchingElements || []) {
                const username = currentMatchingEl.getAttribute(MechanicRestWorkingHoursFeature.DOM_ATTRIBUTE_SELECTOR);
                const trimmedUsername = trim(username || '');
                if (trimmedUsername !== '') {
                    fastElMap[trimmedUsername] = currentMatchingEl as HTMLElement;
                }
            }

            const allUsernames = Object.keys(fastElMap);
            if (allUsernames.length > 0) {
                this.refreshRestWorkingHours(castedConfig, fastElMap, repositionGroupCB);
            }
        });

        return this;
    }

    public destroy() {
        if (this.interval) {
            clearInterval(this.interval);
        }
    }

    private refreshRestWorkingHours(
        config: DisguiseDto.TimelineRestWorkingHoursFeatureConfigDto,
        fastElMap: { [username: string]: HTMLElement },
        repositionGroupCB: () => void
    ) {
        HTTP_SERVICE_INSTANCE.getAxios().get<DisguiseDto.RestWorkingHoursResultsMapDto>(
            '/mechanics/jobs/v0/restworkinghours',
        ).then((response) => {
            if (response.data) {
                if (!this.interval && config.refreshIntervalInSeconds !== undefined) {
                    this.interval = setInterval(() => {
                        this.refreshRestWorkingHours(config, fastElMap, repositionGroupCB);
                    }, config.refreshIntervalInSeconds * 1000);
                }

                const quotaHours = Math.round(moment.duration(response.data.periodQuota || '').asHours());
                const workingHoursUserNames = Object.keys(response.data.results || {});
                for (const currentUserName of workingHoursUserNames) {
                    if (response.data.results) {
                        const currentUserWorkingHoursValue = response.data.results[currentUserName];

                        const htmlElementByUsername = fastElMap[currentUserName];
                        if (htmlElementByUsername) {
                            // found
                            const indicatorEl = window.document.createElement('div');
                            const alreadyWorkedInCurrentPeriodHoursString = Math.round(moment.duration(currentUserWorkingHoursValue.alreadyWorkedInCurrentPeriod || '').asHours()).toString().replaceAll('.', ',');
                            const alreadyWorkedInCurrentMonthHoursMoment = moment.duration(currentUserWorkingHoursValue.alreadyWorkedInCurrentMonth || '');
                            const avgRemainingPerMonthHoursString = Math.round(moment.duration(currentUserWorkingHoursValue.avgRestDurationPerMonth || '').asHours()).toString().replaceAll('.', ',');

                            let capacityThisMonthResult = `${moment().format('MMMM')} zur Verfügung: %VAL%`;
                            let restCapacityThisMonthResult = `Verbleibende Zeit im ${moment().format('MMMM')}: %VAL%`;
                            const avgRemainingPerMonthHoursResult = `Durchschnittlich ${avgRemainingPerMonthHoursString}h pro Monat verbleibend`;
                            if (currentUserWorkingHoursValue.holidaySettings !== undefined
                                && currentUserWorkingHoursValue.holidaySettings.state !== undefined
                                && currentUserWorkingHoursValue.holidaySettings.country !== undefined
                                && currentUserWorkingHoursValue.maxWorkingTimePerDay !== undefined) {

                                const currDate = moment().startOf('month').startOf('day');
                                const lastDate = moment().endOf('month').endOf('day');

                                const holidaysForUserSettings = new Holidays({
                                    country: currentUserWorkingHoursValue.holidaySettings.country,
                                    state: currentUserWorkingHoursValue.holidaySettings.state
                                }, {
                                    types: ['public', 'bank', 'school', 'optional']
                                });
                                const allWorkingDaysThisMonth: Date[] = [];
                                while (currDate.diff(lastDate) <= 0) {
                                    const sunday = currDate.date() === currDate.clone().endOf('week').date();
                                    const isHoliday = holidaysForUserSettings.isHoliday(currDate.toDate());
                                    if (!isHoliday && !sunday) {
                                        allWorkingDaysThisMonth.push(currDate.toDate());
                                    }
                                    currDate.add(1, 'days');
                                }

                                const maxWorkingTimePerDay = moment.duration(currentUserWorkingHoursValue.maxWorkingTimePerDay).asHours();
                                const restCapacityThisMonthValue = Math.round(
                                    moment.duration(allWorkingDaysThisMonth.length * maxWorkingTimePerDay, 'hours').subtract(alreadyWorkedInCurrentMonthHoursMoment).asHours()
                                ).toString().replaceAll('.', ',');
                                const capacityThisMonthValue = Math.round(allWorkingDaysThisMonth.length * maxWorkingTimePerDay).toString().replaceAll('.', ',');
                                restCapacityThisMonthResult = restCapacityThisMonthResult.replace('%VAL%', restCapacityThisMonthValue + 'h');
                                capacityThisMonthResult = capacityThisMonthResult.replace('%VAL%', capacityThisMonthValue + 'h');
                            } else {
                                restCapacityThisMonthResult = restCapacityThisMonthResult.replace('%VAL%', 'Unbekannte Ländereinstellung');
                                capacityThisMonthResult = capacityThisMonthResult.replace('%VAL%', 'Unbekannte Ländereinstellung');
                            }

                            indicatorEl.innerHTML = `${alreadyWorkedInCurrentPeriodHoursString}h / ${quotaHours}h`;
                            indicatorEl.title = `${capacityThisMonthResult}\n${restCapacityThisMonthResult}\n${avgRemainingPerMonthHoursResult}`;

                            if (htmlElementByUsername.firstChild) {
                                htmlElementByUsername.firstChild.remove();
                            }
                            htmlElementByUsername.appendChild(indicatorEl);
                        }
                    }
                }
            }
        });
    }
}
